import getWebsiteUrl from '@cc/shared/utils/getWebsiteUrl';

// Add font-related interfaces
interface FontFace {
    family: string;
    source: string;
    descriptors?: FontFaceDescriptors;
}

// New function to extract used fonts (metadata only, not base64 data)
const extractUsedFonts = async (element: HTMLElement): Promise<FontFace[]> => {
    const usedFonts: FontFace[] = [];
    const usedFontFamilies = new Set<string>();

    // Get all elements that might have fonts applied
    const allElements = [
        element,
        ...element.querySelectorAll('*'),
    ] as HTMLElement[];

    // Collect all font-family values actually used
    allElements.forEach((el) => {
        const computedStyle = window.getComputedStyle(el);
        const fontFamily = computedStyle.fontFamily;

        if (fontFamily && fontFamily !== 'inherit') {
            // Parse font-family (can be comma-separated list)
            const families = fontFamily
                .split(',')
                .map((f) => f.trim().replace(/['"]/g, ''));
            families.forEach((family) => {
                if (
                    !family.includes('system-ui') &&
                    !family.includes('-apple-system') &&
                    !family.includes('sans-serif') &&
                    !family.includes('serif') &&
                    !family.includes('monospace') &&
                    !family.includes('cursive') &&
                    !family.includes('fantasy')
                ) {
                    usedFontFamilies.add(family);
                }
            });
        }
    });

    try {
        // Only extract fonts that are actually used
        for (const styleSheet of document.styleSheets) {
            try {
                const rules = styleSheet.cssRules || styleSheet.rules;
                for (const rule of rules) {
                    if (rule.type === CSSRule.FONT_FACE_RULE) {
                        const fontFaceRule = rule as CSSFontFaceRule;
                        const family = fontFaceRule.style
                            .getPropertyValue('font-family')
                            .replace(/['"]/g, '');

                        // Only include if this font is actually used
                        if (usedFontFamilies.has(family)) {
                            const src = fontFaceRule.style.getPropertyValue('src');
                            if (src && family) {
                                usedFonts.push({
                                    family: family,
                                    source: src,
                                    descriptors: {
                                        style:
                                            fontFaceRule.style.getPropertyValue('font-style') ||
                                            'normal',
                                        weight:
                                            fontFaceRule.style.getPropertyValue('font-weight') ||
                                            'normal',
                                        stretch:
                                            fontFaceRule.style.getPropertyValue('font-stretch') ||
                                            'normal',
                                        unicodeRange:
                                            fontFaceRule.style.getPropertyValue('unicode-range') ||
                                            undefined,
                                        variant:
                                            fontFaceRule.style.getPropertyValue('font-variant') ||
                                            'normal',
                                        featureSettings:
                                            fontFaceRule.style.getPropertyValue(
                                                'font-feature-settings',
                                            ) || undefined,
                                    },
                                });
                            }
                        }
                    }
                }
            } catch (error) {
                console.warn('Cannot access stylesheet for font extraction:', error);
            }
        }
    } catch (error) {
        console.warn('Error extracting fonts from CSS:', error);
    }

    return usedFonts;
};

const generateFontFaceCSS = (fonts: FontFace[]): string => {
    return fonts
        .map((font) => {
            let css = `@font-face {\n  font-family: "${font.family}";\n  src: ${font.source};\n`;

            if (font.descriptors?.style && font.descriptors.style !== 'normal') {
                css += `  font-style: ${font.descriptors.style};\n`;
            }
            if (font.descriptors?.weight && font.descriptors.weight !== 'normal') {
                css += `  font-weight: ${font.descriptors.weight};\n`;
            }
            if (font.descriptors?.stretch && font.descriptors.stretch !== 'normal') {
                css += `  font-stretch: ${font.descriptors.stretch};\n`;
            }
            if (font.descriptors?.unicodeRange) {
                css += `  unicode-range: ${font.descriptors.unicodeRange};\n`;
            }
            if (font.descriptors?.variant && font.descriptors.variant !== 'normal') {
                css += `  font-variant: ${font.descriptors.variant};\n`;
            }
            if (font.descriptors?.featureSettings) {
                css += `  font-feature-settings: ${font.descriptors.featureSettings};\n`;
            }

            css += `}\n`;
            return css;
        })
        .join('\n');
};

const extractHTMLForPDF = async (
    element: HTMLElement,
    options: GeneratePDFOptions = {},
) => {
    const clone = element.cloneNode(true) as HTMLElement;

    const cssRules = extractRelevantCSS(element);

    // Extract only used fonts (metadata only, no base64 conversion)
    const fonts = await extractUsedFonts(element);
    const fontCSS = generateFontFaceCSS(fonts);

    const customCssCode = document.getElementById('cc-custom-css')?.innerText;

    const links = document.head.querySelectorAll('link');
    const stylesheets = Array.from(links).filter(
        (link) => {
            return link.rel === 'stylesheet';
        },
    );

    return createCompleteHTML(
        clone.outerHTML,
        cssRules,
        options,
        fontCSS,
        stylesheets,
        customCssCode,
    );
};

const extractRelevantCSS = (element: HTMLElement) => {
    const relevantRules = new Set<string>();
    const allElements = [element, ...element.querySelectorAll('*')];

    // Extract inline styles as backup
    const inlineStyles = new Set<string>();
    allElements.forEach((el) => {
        if (el instanceof HTMLElement && el.style && el.style.cssText) {
            const selector =
                el.tagName.toLowerCase() +
                (el.className ? `.${el.className.split(' ').join('.')}` : '') +
                (el.id ? `#${el.id}` : '');
            inlineStyles.add(`${selector} { ${el.style.cssText} }`);
        }
    });

    try {
        for (const styleSheet of document.styleSheets) {
            try {
                const rules = styleSheet.cssRules || styleSheet.rules;
                for (const rule of rules) {
                    if (rule.type === CSSRule.STYLE_RULE) {
                        if (
                            allElements.some((el) => {
                                try {
                                    return el.matches((rule as CSSStyleRule).selectorText);
                                } catch (e) {
                                    return false;
                                }
                            })
                        ) {
                            relevantRules.add(rule.cssText);
                        }
                    }
                    // Include @media rules for responsive design
                    else if (rule.type === CSSRule.MEDIA_RULE) {
                        try {
                            const mediaRule = rule as CSSMediaRule;
                            for (const nestedRule of mediaRule.cssRules) {
                                if (nestedRule.type === CSSRule.STYLE_RULE) {
                                    if (
                                        allElements.some((el) => {
                                            try {
                                                return el.matches(
                                                    (nestedRule as CSSStyleRule).selectorText,
                                                );
                                            } catch (e) {
                                                return false;
                                            }
                                        })
                                    ) {
                                        relevantRules.add(rule.cssText);
                                        break;
                                    }
                                }
                            }
                        } catch (e) {
                            // Add the entire media rule if we can't parse it
                            relevantRules.add(rule.cssText);
                        }
                    }
                    // Skip @font-face rules here as we handle them separately
                }
            } catch (e) {
                console.warn('Cannot access stylesheet:', e);
            }
        }
    } catch (e) {
        console.warn('Error extracting CSS:', e);
    }

    // Combine stylesheet rules with inline styles
    const allRules = [...Array.from(relevantRules), ...Array.from(inlineStyles)];
    return allRules.join('\n');
};

const createCompleteHTML = (
    bodyHTML: string,
    cssRules = '',
    options: GeneratePDFOptions = {},
    fontCSS = '',
    stylesheets: HTMLLinkElement[] = [],
    customCssCode = '',
) => {
    const { margin = 1.5 } = options;

    const printStyles = `
        @page {
            size: A4;
            margin: ${margin}cm;
        }

        html, body {
            margin: 0;
            padding: 0;
            width: 100%;
            height: 100%;
        }

        * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            word-wrap: normal;
            white-space: normal;
        }
        p, div, span {
            word-break: normal;
            overflow-wrap: normal;
            hyphens: none;
        }
        span, div, td, th {
            font-variant-numeric: lining-nums;
        }
        img, svg {
            max-width: 100%;
            height: auto;
            display: block;
        }
        .print-button {
            display: none;
        }
    `;

    // More comprehensive text sanitization
    const sanitizedHTML = bodyHTML
        .replace(
            /(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4})/g,
            (match) =>
                `<span style="font-family: Arial, sans-serif; font-variant-numeric: lining-nums;">${match}</span>`,
        )
        // Fix any problematic Unicode characters
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/[\u2013\u2014]/g, '-')
        .replace(/\u2026/g, '...');

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
    <title>PDF Content</title>

    ${stylesheets.map((link) => link.outerHTML).join('\n')}

    <style>
        ${fontCSS}
    </style>
    <style>
        ${cssRules}
    </style>
    <style>
        ${customCssCode}
    </style>
    <style>
        ${printStyles}
    </style>
</head>
<body>
    ${sanitizedHTML}
</body>
</html>`;
};

export type GeneratePDFOptions = {
    margin?: number;
};

const generatePDF = async (
    element: HTMLElement,
    options: GeneratePDFOptions,
): Promise<Blob | null> => {
    try {
        const content = await extractHTMLForPDF(element, options);
        const response = await fetch(
            getWebsiteUrl({ slug: '/api/embed/generate-pdf/' }),
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ content }),
            },
        );
        if (!response.ok) {
            throw new Error(`PDF generation failed: ${response.statusText}`);
        }
        return response.blob();
    } catch (error) {
        console.error('PDF generation failed:', error);
        return null;
    }
};

export default generatePDF;
